import {Component, HostBinding, inject, Input, input, InputSignal, Signal, viewChild} from '@angular/core';
import {
  DsButtonModule,
  DsIconModule,
  DsProgressCircleModule,
  DsTooltipDirectiveModule
} from '@bmw-ds/components';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {MileageDataSource} from '@service-and-repairs/awpintegrationlib';
import {ServiceCase} from '../../../../core/service-case/models/service-case';
import {ServiceCaseHolder} from '../../../../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../../../../core/service-case/models/service-case-manager';
import {UserData} from '../../../../core/user/models/user-data';
import {VehicleService} from '../../../../core/vehicle/services/vehicle.service';
import {VehicleDataOverlayComponent} from '../../../vehicle/vehicle-data-overlay/vehicle-data-overlay.component';
import {VehiclePreviewRotationDirective} from '../../../vehicle/vehicle-preview-rotation.directive';
import {
  ServiceCaseAddVinOverlayComponent
} from '../service-case-add-vin-overlay/service-case-add-vin-overlay.component';
import {ServiceCaseReloadOverlayComponent} from '../service-case-reload-overlay/service-case-reload-overlay.component';

@Component({
  selector: 'app-service-case-tab',
  templateUrl: './service-case-tab.component.html',
  styleUrls: ['./service-case-tab.component.scss'],
  imports: [
    DsTooltipDirectiveModule,
    VehicleDataOverlayComponent,
    VehiclePreviewRotationDirective,
    DsIconModule,
    DsProgressCircleModule,
    DsButtonModule,
    ServiceCaseAddVinOverlayComponent,
    ServiceCaseReloadOverlayComponent,
    TranslateModule
  ]
})
export class ServiceCaseTabComponent {
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly translate: TranslateService = inject(TranslateService);
  readonly vehicleService: VehicleService = inject(VehicleService);

  readonly serviceCase: InputSignal<ServiceCase> = input<ServiceCase>();
  readonly user: InputSignal<UserData> = input<UserData>();

  // HostBinding is not supported with input signal
  // https://github.com/angular/angular/issues/53888
  @Input()
  @HostBinding('class.tooltip')
  isTooltip: boolean;

  @Input()
  @HostBinding('class.large')
  isLarge: boolean;

  private readonly caseReloadOverlay: Signal<ServiceCaseReloadOverlayComponent> =
    viewChild<ServiceCaseReloadOverlayComponent>('reloadOverlay');

  private readonly addVinOverlay: Signal<ServiceCaseAddVinOverlayComponent> =
    viewChild<ServiceCaseAddVinOverlayComponent>('addVinOverlay');

  canAddVin(): boolean {
    const sc: ServiceCase = this.serviceCase();
    return !sc.getVehicle()
      && !sc.getVinLong()
      && !sc.getVinShort()
      && !sc.getTypeCode()
      && (sc.getCreatedBySystem() === 'SCHEDULING_SYSTEM' || sc.getCreatedBySystem() === 'ONLINE_SCHEDULING_SYSTEM');
  }

  onAddVinClick(): void {
    this.addVinOverlay().show(this.serviceCase());
  }

  onReloadClick(): void {
    if (this.serviceCase().getVinLong()) {
      this.serviceCaseManager.reloadServiceCaseById(this.serviceCase().getExternalId(), false)
        .finally(() => {
          // do nothing
        });
    } else {
      this.caseReloadOverlay().show(this.serviceCase());
    }
  }

  onCloseClick(): void {
    this.serviceCaseHolder.closeServiceCaseById(this.serviceCase().getExternalId(), 'webapp');
  }

  getWarningsTooltip(): string {
    return this.serviceCase().getWarnings()
      .filter(warning => !warning.isStop)
      .map(warning => {
        if (warning.message?.toLowerCase().includes('alpina')) {
          // only show first line of Alpina warning in tooltip
          return warning.message?.split('<br>')[0];
        } else {
          return warning.message;
        }
      })
      // create new line for each warning
      .join('\n');
  }

  getSalesStopTooltip(): string {
    return this.serviceCase().getWarnings()
      .filter(warning => warning.isStop)
      .map(warning => warning.message)
      // usually there is only one sales stop, use line break anyway
      .join('\n');
  }

  showTabImage(): boolean {
    return !!this.serviceCase().getSmallCosyImageUrl()
      && !this.serviceCase().vehicleDataMissing()
      && !this.isTooltip;
  }

  getMileageSourceText(source: MileageDataSource): string {
    switch (source) {
      case MileageDataSource.KEY_READER:
      case MileageDataSource.REMOTE_KEY_READ:
      case MileageDataSource.TELESERVICES:
        return this.translate.instant('vehicle.mileageSourceKey');
      case MileageDataSource.VEHICLE_HISTORY:
        return this.translate.instant('vehicle.mileageSourceFBM');
      case MileageDataSource.OVER_THE_AIR:
        return this.translate.instant('vehicle.mileageSourceVS');
      case MileageDataSource.MANUAL:
        return this.translate.instant('vehicle.mileageSourceManual');
      default:
        return this.translate.instant('vehicle.mileageSourceOther');
    }
  }
}
